import type { KeyboardEvent, MouseEvent } from 'react';
import { useRef, useState } from 'react';
import { FaCheck } from 'react-icons/fa';
import { HiClock } from 'react-icons/hi2';
import { useNavigate } from 'react-router-dom';
import { readReadingProgress } from '../utils/readingProgress';
import type { TopicMetadata } from '../types';
import TopicTagList from './TopicTagList';

interface TopicMetadataCardProps {
    topic: TopicMetadata;
    themeSlug: string;
    topicNumber: number;
}

interface Ripple {
    x: number;
    y: number;
    id: number;
}

export default function TopicMetadataCard({ topic, themeSlug, topicNumber }: TopicMetadataCardProps) {
    const [ripples, setRipples] = useState<Ripple[]>([]);
    const cardRef = useRef<HTMLDivElement>(null);
    const rippleIdRef = useRef(0);
    const navigate = useNavigate();
    const isCompleted = Boolean(readReadingProgress(topic.slug)?.completed);

    const navigateToTopic = () => {
        navigate(`/thematic-studies/${themeSlug}/${topic.slug}`);
    };

    const handleClick = (event: MouseEvent<HTMLDivElement>) => {
        if (!cardRef.current) return;

        event.preventDefault();

        const rect = cardRef.current.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;
        const id = rippleIdRef.current++;

        setRipples((previous) => [...previous, { x, y, id }]);

        window.setTimeout(() => {
            setRipples((previous) => previous.filter((ripple) => ripple.id !== id));
        }, 600);

        window.setTimeout(navigateToTopic, 150);
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            navigateToTopic();
        }
    };

    return (
        <div
            ref={cardRef}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            tabIndex={0}
            role="link"
            aria-label={`Read topic: ${topic.title}`}
            className="group relative block cursor-pointer overflow-hidden rounded-lg border border-gray-200 bg-white shadow-md transition-all duration-300 hover:scale-[1.01] hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-darkBlue focus:ring-offset-2 dark:border-gray-700 dark:bg-gray-900 dark:focus:ring-lightBlue"
        >
            {isCompleted && (
                <span
                    className="absolute right-2 top-2 z-10 flex h-6 w-6 items-center justify-center rounded-full bg-darkBlue text-white dark:bg-lightBlue"
                    title="Completed"
                    aria-label="Completed"
                >
                    <FaCheck className="h-3.5 w-3.5" />
                </span>
            )}

            <div className="p-6">
                {topic.series && (
                    <div className="mb-3">
                        <div className="inline-flex items-center rounded-full bg-lightBlue px-3 py-1 text-xs font-medium text-white dark:bg-lightBlue/20 dark:text-lightBlue">
                            {topic.series.name} | Part {topic.series.part} of {topic.series.totalParts}
                        </div>
                    </div>
                )}

                <div className="mb-3 flex items-center justify-between gap-3">
                    <h3 className="line-clamp-2 min-w-0 flex-1 text-2xl font-bold leading-tight text-darkBlue transition-colors group-hover:text-lightBlue dark:text-lightBlue dark:group-hover:text-lightBlue">
                        {topic.title}
                    </h3>
                    <div className="flex-shrink-0">
                        <div className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-red/70 text-sm font-semibold text-white font-inter-numbers">
                            #{topicNumber}
                        </div>
                    </div>
                </div>

                <p className="mb-4 line-clamp-3 text-sm leading-relaxed text-default-600 dark:text-default-400">
                    {topic.shortDescription}
                </p>

                <TopicTagList tags={topic.tags} className="mb-4" />

                <div className="flex items-center justify-between gap-4 border-t border-gray-200 pt-4 dark:border-gray-700">
                    <div className="flex items-center gap-2 text-sm text-default-500 dark:text-default-400">
                        <HiClock className="h-4 w-4 flex-shrink-0" />
                        <span className="font-inter-numbers">{topic.estimatedReadTime} min read</span>
                    </div>
                    <span className="text-sm font-medium text-darkBlue transition-colors group-hover:text-lightBlue dark:text-lightBlue dark:group-hover:text-lightBlue">
                        Read More →
                    </span>
                </div>
            </div>

            {ripples.map((ripple) => (
                <span
                    key={ripple.id}
                    className="pointer-events-none absolute rounded-full bg-white/30 dark:bg-white/20 animate-ripple"
                    style={{
                        left: ripple.x,
                        top: ripple.y,
                        transform: 'translate(-50%, -50%)',
                    }}
                />
            ))}
        </div>
    );
}
